/** Persist column prefs per list (+ company) in localStorage — plan/34. */

export type DataTableViewMode = "auto" | "table" | "stacked";

export type DataTablePrefs = {
  columnVisibility?: Record<string, boolean>;
  columnOrder?: string[];
  columnSizing?: Record<string, number>;
  /**
   * Layout preference. `auto` = table on wide, stacked on narrow.
   * Legacy `forceTable: true` loads as `table`.
   */
  viewMode?: DataTableViewMode;
  /** @deprecated use viewMode — kept for reading older localStorage */
  forceTable?: boolean;
  /**
   * Show muted relative time under Date (docDate) cells — plan/35.
   * Default ON when unset.
   */
  showRelativeTime?: boolean;
};

/** Default ON — plan/35 list chrome. */
export function resolveShowRelativeTime(prefs: DataTablePrefs): boolean {
  return prefs.showRelativeTime !== false;
}

export function dataTablePrefsKey(listId: string, companySlug: string): string {
  return `icraftx.datatable.${companySlug}.${listId}`;
}

export function resolveViewMode(prefs: DataTablePrefs): DataTableViewMode {
  if (
    prefs.viewMode === "auto" ||
    prefs.viewMode === "table" ||
    prefs.viewMode === "stacked"
  ) {
    return prefs.viewMode;
  }
  if (prefs.forceTable) return "table";
  return "auto";
}

export function loadDataTablePrefs(
  listId: string,
  companySlug: string,
): DataTablePrefs {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(
      dataTablePrefsKey(listId, companySlug),
    );
    if (!raw) return {};
    const parsed = JSON.parse(raw) as DataTablePrefs;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveDataTablePrefs(
  listId: string,
  companySlug: string,
  prefs: DataTablePrefs,
): void {
  if (typeof window === "undefined") return;
  try {
    const { forceTable: _legacy, ...rest } = prefs;
    void _legacy;
    window.localStorage.setItem(
      dataTablePrefsKey(listId, companySlug),
      JSON.stringify(rest),
    );
  } catch {
    /* quota / private mode — ignore */
  }
}

export function clearDataTablePrefs(
  listId: string,
  companySlug: string,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(dataTablePrefsKey(listId, companySlug));
  } catch {
    /* ignore */
  }
}
